import { useCallback, useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2, LogOut, Inbox, Trash2, Mail, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "../components/Logo";
import { apiFetch } from "../lib/api";

const STATUS_STYLES = {
  new: "border-cyan-500/40 bg-cyan-500/10 text-cyan-300",
  "in-progress": "border-amber-500/40 bg-amber-500/10 text-amber-300",
  done: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300",
};

export default function AdminDashboard() {
  const [user, setUser] = useState(null);
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    const res = await apiFetch("/enquiries");
    if (res.ok) setEnquiries(await res.json());
    setLoading(false);
  }, []);

  useEffect(() => {
    apiFetch("/auth/me").then(async (r) => {
      if (r.ok) {
        setUser(await r.json());
      } else {
        navigate("/admin/login");
      }
    });
  }, [navigate]);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  const setStatus = async (id, status) => {
    const res = await apiFetch(`/enquiries/${id}`, { method: "PATCH", body: JSON.stringify({ status }) });
    if (res.ok) {
      setEnquiries((prev) => prev.map((e) => (e.id === id ? { ...e, status } : e)));
      toast.success(`Marked as ${status}`);
    } else toast.error("Could not update status");
  };

  const remove = async (id) => {
    const res = await apiFetch(`/enquiries/${id}`, { method: "DELETE" });
    if (res.ok) {
      setEnquiries((prev) => prev.filter((e) => e.id !== id));
      toast.success("Enquiry deleted");
    } else toast.error("Could not delete enquiry");
  };

  const logout = async () => {
    await apiFetch("/auth/logout", { method: "POST" });
    navigate("/admin/login");
  };

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-canvas" data-testid="admin-loading">
        <Loader2 size={28} className="animate-spin text-cyan-400" />
      </div>
    );
  }

  const filtered = filter === "all" ? enquiries : enquiries.filter((e) => e.status === filter);
  const counts = {
    all: enquiries.length,
    new: enquiries.filter((e) => e.status === "new").length,
    "in-progress": enquiries.filter((e) => e.status === "in-progress").length,
    done: enquiries.filter((e) => e.status === "done").length,
  };

  return (
    <div className="min-h-screen bg-canvas" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 border-b border-cyan-500/15 bg-[#070B19]/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4">
          <Link to="/" data-testid="admin-logo">
            <Logo compact />
          </Link>
          <div className="flex items-center gap-3">
            <button
              onClick={load}
              data-testid="admin-refresh-button"
              className="rounded-lg border border-white/10 p-2 text-slate-400 transition-colors hover:text-cyan-300"
              aria-label="Refresh enquiries"
            >
              <RefreshCw size={16} />
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs font-medium text-slate-300 transition-colors hover:border-red-400/40 hover:text-red-300"
            >
              <LogOut size={14} />
              Log out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 py-10">
        <h1 className="font-display text-2xl font-bold text-slate-50">Enquiries</h1>
        <p className="mt-1 text-sm text-slate-400">Signed in as {user.email}</p>

        <div className="mt-7 flex flex-wrap gap-2" data-testid="admin-filters">
          {["all", "new", "in-progress", "done"].map((f) => (
            <button
              key={f}
              data-testid={`admin-filter-${f}`}
              onClick={() => setFilter(f)}
              className={`rounded-full border px-4 py-2 text-xs font-medium capitalize transition-colors ${
                filter === f
                  ? "border-cyan-400/60 bg-cyan-500/15 text-cyan-300"
                  : "border-white/10 text-slate-400 hover:text-slate-200"
              }`}
            >
              {f} ({counts[f]})
            </button>
          ))}
        </div>

        {loading ? (
          <div className="mt-16 flex justify-center">
            <Loader2 size={26} className="animate-spin text-cyan-400" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="mt-16 flex flex-col items-center text-center" data-testid="admin-empty-state">
            <Inbox size={36} className="text-slate-600" />
            <p className="mt-4 text-sm text-slate-500">No enquiries here yet.</p>
          </div>
        ) : (
          <div className="mt-6 space-y-4" data-testid="admin-enquiries-list">
            {filtered.map((e) => (
              <article
                key={e.id}
                data-testid={`admin-enquiry-${e.id}`}
                className="rounded-2xl border border-white/10 bg-white/[0.03] p-6"
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h2 className="font-display text-base font-semibold text-slate-50">{e.name}</h2>
                    <a href={`mailto:${e.email}`} className="mt-0.5 inline-flex items-center gap-1.5 text-xs text-cyan-300 hover:underline">
                      <Mail size={12} />
                      {e.email}
                    </a>
                  </div>
                  <span className={`rounded-full border px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] ${STATUS_STYLES[e.status] || STATUS_STYLES.new}`}>
                    {e.status}
                  </span>
                </div>
                <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.15em] text-slate-500">
                  {e.service} · {new Date(e.created_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                </p>
                {e.estimate && <p className="mt-2 text-xs text-slate-500">{e.estimate}</p>}
                <p className="mt-3 whitespace-pre-wrap rounded-xl bg-white/[0.03] p-4 text-sm leading-relaxed text-slate-300">
                  {e.message}
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {["new", "in-progress", "done"]
                    .filter((s) => s !== e.status)
                    .map((s) => (
                      <button
                        key={s}
                        data-testid={`admin-status-${s}-${e.id}`}
                        onClick={() => setStatus(e.id, s)}
                        className="rounded-full border border-white/10 px-3.5 py-1.5 text-xs text-slate-400 transition-colors hover:border-cyan-400/40 hover:text-cyan-300"
                      >
                        Mark {s}
                      </button>
                    ))}
                  <button
                    data-testid={`admin-delete-${e.id}`}
                    onClick={() => remove(e.id)}
                    className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-white/10 px-3.5 py-1.5 text-xs text-slate-500 transition-colors hover:border-red-400/40 hover:text-red-300"
                  >
                    <Trash2 size={12} />
                    Delete
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
